/**
 * Everything the pills read, write and subscribe to (deliverable 14), behind one interface so a
 * story makes no network request. The pills never call `api`, `useTypes()` or `appEvents`
 * directly. Image upload is not offered: `assets.upload` needs a `blockId` a field has none of
 * (open point 4).
 */
import {
  type AppEventV1,
  type DocTitleV1,
  type DocTypeV1,
  type DocumentV1,
  docsGet,
  docsPatchMeta,
  docsQuarantine,
  docsRetype,
  docsRetypePreview,
  docsTitles,
  type LinkCreateResultV1,
  type LinkCreateV1,
  type LinkTypeV1,
  type LinkV1,
  linksCreate,
  linksList,
  linksRemove,
  linksTypes,
  type PatchMetaResponseV1,
  type PickerResultV1,
  type PropValueV1,
  type QuarantinedValueV1,
  type RetypeReportV1,
  type RetypeRequestV1,
  searchPicker,
  typesList,
} from '@codex/contracts';
import type { ApiClient } from '@codex/contracts/client';
import { createContext, useContext } from 'react';
import { api } from '../../auth/client.js';
import { appEvents } from '../../editor-services/events.js';
import { undoable } from '../../undo/undoable.js';

/** The events the pills watch. */
export type PillEventType = 'doc.changed' | 'links.changed' | 'types.changed';
export type PillEvent<T extends PillEventType> = Extract<AppEventV1, { type: T }>;

export interface PillServices {
  getDocument(docId: string): Promise<DocumentV1>;
  /** Both directions, every link type. */
  listLinks(docId: string): Promise<LinkV1[]>;
  listTypes(): Promise<DocTypeV1[]>;
  listLinkTypes(): Promise<LinkTypeV1[]>;
  titles(ids: readonly string[]): Promise<DocTitleV1[]>;
  quarantine(docId: string): Promise<QuarantinedValueV1[]>;
  /** Canon pages whose title matches. */
  searchPicker(q: string): Promise<PickerResultV1>;
  /** One field's value; last writer wins (no `baseMetaVersion`). */
  patchProp(docId: string, fieldDefId: string, value: PropValueV1): Promise<PatchMetaResponseV1>;
  retypePreview(docId: string, body: RetypeRequestV1): Promise<RetypeReportV1>;
  retype(docId: string, body: RetypeRequestV1): Promise<RetypeReportV1>;
  /** `sourceId` is the document whose path carries the link. */
  createLink(sourceId: string, body: LinkCreateV1): Promise<LinkCreateResultV1>;
  removeLink(sourceId: string, link: Pick<LinkV1, 'id' | 'rv'>): Promise<void>;
  /** Returns the unsubscribe. */
  subscribe<T extends PillEventType>(type: T, listener: (event: PillEvent<T>) => void): () => void;
}

/** The titles request takes at most this many ids. */
export const TITLES_PER_REQUEST = 500;

export function createPillServices(client: ApiClient = api): PillServices {
  return {
    getDocument: (docId) => client(docsGet, { params: { id: docId } }),
    listLinks: async (docId) =>
      (await client(linksList, { params: { id: docId }, query: {} })).items,
    listTypes: async () => (await client(typesList)).items,
    listLinkTypes: async () => (await client(linksTypes)).items,
    titles: async (ids) => {
      const found: DocTitleV1[] = [];
      for (let start = 0; start < ids.length; start += TITLES_PER_REQUEST) {
        const answer = await client(docsTitles, {
          body: { ids: ids.slice(start, start + TITLES_PER_REQUEST) },
        });
        found.push(...answer.titles);
      }
      return found;
    },
    quarantine: async (docId) => (await client(docsQuarantine, { params: { id: docId } })).items,
    searchPicker: (q) => client(searchPicker, { query: { q, limit: 8 } }),
    patchProp: (docId, fieldDefId, value) =>
      client(
        docsPatchMeta,
        { params: { id: docId }, body: { field: 'prop', fieldDefId, value } },
        undoable('docs.patchMeta'),
      ),
    retypePreview: (docId, body) => client(docsRetypePreview, { params: { id: docId }, body }),
    retype: (docId, body) => client(docsRetype, { params: { id: docId }, body }),
    createLink: (sourceId, body) => client(linksCreate, { params: { id: sourceId }, body }),
    removeLink: async (sourceId, link) => {
      await client(linksRemove, {
        params: { id: sourceId, linkId: link.id },
        query: { baseRv: link.rv },
      });
    },
    subscribe: (type, listener) => appEvents.subscribe(type, listener),
  };
}

let shared: PillServices | null = null;

/** The services the pills use when no provider is above them: the real ones, made once. */
export const PillServicesContext = createContext<PillServices | null>(null);

export function usePillServices(): PillServices {
  const provided = useContext(PillServicesContext);
  if (provided) return provided;
  shared ??= createPillServices();
  return shared;
}
